import { Box, Button, CircularProgress, Grid, TextField } from '@mui/material';
import { useMutation, useQuery } from '@parkchirp/api-client';
import { useCallback, useContext, useEffect, useState } from 'react';

import { CommonContext } from '../../contexts';
import { CREATE_HOURLY_ACCOUNT_V2, GET_HOURLY_ACCOUNT_V2, UPDATE_HOURLY_ACCOUNT_V2 } from '../../queries';
import { HourlyAccount, HourlyAccountConfig, HourlyAccountUserGroup } from '../../types';
import { FacilityAutocomplete } from '../inputs';
import { PortalConfigsEditor } from './portalConfigsEditor';
import { PortalFeaturesEditor } from './portalFeaturesEditor';
import { PortalUsersEditor } from './portalUsersEditor';
import { PortalValidationsTable } from './portalValidationsTable';

export interface PortalAccountDetailsProps {
	accountId?: string;
	canEdit?: boolean;
	afterSave?: ({ accountId }: { accountId?: string }) => void;
}

export const PortalAccountDetails = ({ accountId, afterSave, canEdit = true }: PortalAccountDetailsProps) => {
	const { organizationId, setShowSnackbar, apolloHeaders } = useContext(CommonContext);

	const [model, setModel] = useState<HourlyAccount>({});
	const [primaryUserGroup, setPrimaryUserGroup] = useState<HourlyAccountUserGroup>({});
	const [hourlyAccountConfigs, setHourlyAccountConfigs] = useState<HourlyAccountConfig[]>([]);
	const [hasErrors, setHasErrors] = useState(false);
	const [saving, setSaving] = useState(false);
	const [generalAndAccessControlEditing, setGeneralAndAccessControlEditing] = useState(!accountId);
	const [accountConfigEditing, setAccountConfigEditing] = useState(false);
	const [lastSave, setLastSave] = useState<number | null>(null);

	const {
		data,
		loading,
		refetch: refetchHourlyAccount,
		error: loadingError,
	} = useQuery(GET_HOURLY_ACCOUNT_V2, {
		variables: {
			id: accountId,
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
		fetchPolicy: 'network-only',
		ssr: false,
		skip: !accountId,
	});

	const [createHourlyAccount] = useMutation(CREATE_HOURLY_ACCOUNT_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
	});
	const [updateHourlyAccount] = useMutation(UPDATE_HOURLY_ACCOUNT_V2, {
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
			appHeaders: apolloHeaders,
		},
	});

	const syncModel = useCallback(
		(response) => {
			setModel(response.getHourlyAccount);

			setPrimaryUserGroup(
				response.getHourlyAccount.hourlyAccountUserGroups.find((userGroup) => userGroup.masterPolicy),
			);
			setHourlyAccountConfigs(response.getHourlyAccount.hourlyAccountConfigs);
		},
		[setModel, setPrimaryUserGroup],
	);

	useEffect(() => {
		if (!loading && data) {
			syncModel(data);
		}
	}, [loading, data, syncModel]);

	const handleValidate = useCallback(() => {
		if (!model.facility?.id || !model.name) {
			setHasErrors(true);
			return false;
		}

		return true;
	}, [model]);

	const handleCancel = useCallback(() => {
		setGeneralAndAccessControlEditing(false);
		syncModel(data);
	}, [setGeneralAndAccessControlEditing, syncModel, data]);

	const handleSave = useCallback(() => {
		if (!handleValidate()) {
			return;
		}

		const handlers = {
			onCompleted: (response) => {
				setGeneralAndAccessControlEditing(false);
				setSaving(false);
				setShowSnackbar({
					message: `Hourly account ${!accountId ? 'created' : 'updated'} successfully`,
					severity: 'success',
				});
				if (afterSave) {
					afterSave({ accountId: response.createHourlyAccount });
				}
				setLastSave(Number(new Date()));
				refetchHourlyAccount();
			},
			onError: (apolloError) => {
				setSaving(false);
				setShowSnackbar({
					message: apolloError.message,
					severity: 'error',
				});
			},
		};

		const input = {
			name: model.name,
			description: model.description,
			facilityId: model.facility?.id,
			organizationId: model.organizationSettings.organizationId,
			masterPolicy: {
				features:
					primaryUserGroup.userGroup?.functionGroups?.map(
						(functionGroups) => functionGroups.functionGroup.id,
					) ?? [],
				validations:
					primaryUserGroup.hourlyAccountUserGroupZonePromoCodes?.map((zonePromoCode) => ({
						id: zonePromoCode.id,
						zonePromoCodeId: zonePromoCode.zonePromoCode.id,
						webValidation: zonePromoCode.webValidation,
						qrValidation: zonePromoCode.qrValidation,
					})) ?? [],
			},
		};

		setSaving(true);

		if (!accountId) {
			createHourlyAccount({
				variables: {
					input,
				},
				...handlers,
			});
		} else {
			updateHourlyAccount({
				variables: {
					input: {
						id: accountId,
						...input,
					},
				},
				...handlers,
			});
		}
	}, [
		model,
		accountId,
		updateHourlyAccount,
		createHourlyAccount,
		primaryUserGroup,
		handleValidate,
		afterSave,
		setShowSnackbar,
	]);

	const handleFieldChanges = useCallback(
		(modification) => {
			setModel((prevModel) => ({ ...prevModel, ...modification }));
		},
		[model, setModel],
	);

	const handleFacilitySelection = useCallback(
		(facility) => {
			if (facility) {
				handleFieldChanges({
					facility: {
						id: facility.id,
						facilityName: facility.facilityName,
						facilityNumber: facility.facilityNumber,
					},
					organizationSettings: {
						organizationId: facility.organizationId,
					},
				});
			} else {
				handleFieldChanges({
					facility: null,
					organizationId: null,
				});
			}

			setPrimaryUserGroup({
				...primaryUserGroup,
				hourlyAccountUserGroupZonePromoCodes: [],
			});
		},
		[primaryUserGroup, setPrimaryUserGroup, handleFieldChanges],
	);

	useEffect(() => {
		if (loadingError) {
			setShowSnackbar({
				message: 'Could not load hourly account data',
				severity: 'error',
			});
		}
	}, [loadingError, setShowSnackbar]);

	if (loading) {
		return <CircularProgress />;
	}

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Box sx={{ display: 'flex' }}>
					<h3 style={{ marginBottom: '10px' }}>General</h3>
					{canEdit && (
						<Grid container sx={{ marginTop: '5px', justifyContent: 'end' }}>
							<Grid item>
								{!generalAndAccessControlEditing && (
									<Button
										size='small'
										disabled={loading || accountConfigEditing}
										onClick={() => {
											setGeneralAndAccessControlEditing(true);
										}}
									>
										Edit
									</Button>
								)}
								{generalAndAccessControlEditing && (
									<>
										<Button
											size='small'
											variant='outlined'
											onClick={handleCancel}
											disabled={saving}
										>
											Cancel
										</Button>
										<Button size='small' onClick={handleSave} disabled={saving}>
											Save
											{saving && <CircularProgress sx={{ ml: 1 }} size={18} />}
										</Button>
									</>
								)}
							</Grid>
						</Grid>
					)}
				</Box>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<TextField
							size='small'
							label='Account Name'
							variant='outlined'
							InputLabelProps={{ shrink: true }}
							onChange={(event) => handleFieldChanges({ name: event.target.value })}
							value={model?.name ?? ''}
							error={hasErrors && !model.name}
							disabled={!generalAndAccessControlEditing}
							sx={{ width: '25%' }}
						/>
					</Grid>
					<Grid item xs={6}>
						<FacilityAutocomplete
							required
							disabled={!!accountId || !canEdit}
							error={hasErrors && !model.facility?.id}
							label='Facility'
							facilityId={model?.facility?.id}
							facilityName={model?.facility?.facilityName}
							facilityNumber={model?.facility?.facilityNumber?.toString()}
							organizationId={organizationId}
							onChange={handleFacilitySelection}
							size='small'
						/>
					</Grid>
				</Grid>
			</Grid>

			<Grid item xs={12}>
				<h3 style={{ marginTop: '10px', marginBottom: '10px' }}>Access Control</h3>
				<Box
					sx={{
						px: 3,
						py: 2,
						border: '1px solid rgba(0,0,0,.2)',
						borderRadius: '5px',
						opacity: !generalAndAccessControlEditing ? 0.5 : 1,
						backgroundColor: !generalAndAccessControlEditing ? '#f8f8f8' : 'none',
					}}
				>
					<PortalValidationsTable
						facilityId={model?.facility?.id}
						zonePromoCodes={primaryUserGroup?.hourlyAccountUserGroupZonePromoCodes ?? []}
						canEdit={generalAndAccessControlEditing}
						canAdd={canEdit && generalAndAccessControlEditing}
						onChange={(validations) =>
							setPrimaryUserGroup({
								...primaryUserGroup,
								hourlyAccountUserGroupZonePromoCodes: validations,
							})
						}
					/>
					<PortalFeaturesEditor
						functionGroups={primaryUserGroup?.userGroup?.functionGroups}
						parentFunctionGroups={!canEdit ? primaryUserGroup?.userGroup?.functionGroups : null}
						canAdd={canEdit}
						canEdit={generalAndAccessControlEditing}
						onChange={(functionGroups) => {
							setPrimaryUserGroup({
								...primaryUserGroup,
								userGroup: {
									functionGroups,
								},
							});
						}}
					/>
				</Box>
			</Grid>

			<Grid item xs={12}>
				<h3 style={{ marginTop: '10px', marginBottom: '10px' }}>Account Configuration</h3>
				<Box
					sx={{
						px: 3,
						py: 2,
						border: '1px solid rgba(0,0,0,.2)',
						borderRadius: '5px',
						backgroundColor: !accountConfigEditing ? '#f8f8f8' : 'none',
					}}
				>
					<PortalConfigsEditor
						readonlyConfigs={hourlyAccountConfigs}
						facilityId={model?.facility?.id}
						isAccountConfigEditing={accountConfigEditing}
						setAccountConfigEditing={setAccountConfigEditing}
						isPageEditing={generalAndAccessControlEditing}
						accountId={accountId}
						isParentLoading={loading}
						refetchHourlyAccount={refetchHourlyAccount}
						readonlyPrimaryUserGroup={data?.getHourlyAccount.hourlyAccountUserGroups.find(
							(userGroup) => userGroup.masterPolicy,
						)}
					/>
				</Box>
			</Grid>

			<Grid item xs={12}>
				<PortalUsersEditor
					disabled={generalAndAccessControlEditing}
					hourlyAccount={data?.getHourlyAccount}
					features={primaryUserGroup?.userGroup?.functionGroups?.map(
						(functionGroup) => functionGroup.functionGroup,
					)}
					validations={primaryUserGroup.hourlyAccountUserGroupZonePromoCodes}
					refetchToken={lastSave}
				/>
			</Grid>
		</Grid>
	);
};
